import { Badge, Tooltip } from '@mantine/core';
import {
    AUDIT_OUTCOME,
    OP_RESULT,
    PLAN_STATUS,
    SYNC_STATUS,
    USER_STATUS,
    type AuditOutcome,
    type OpResult,
    type PlanStatus,
    type StatusMeta,
    type SyncStatus,
    type UserStatus,
} from '../../domain/status.js';
import { glyphIcon } from './glyphs.js';

/**
 * Every state in the app renders through here, off the maps in `domain/status.ts`.
 *
 * A badge rather than a dot: in a matrix cell the status IS the content, so it gets the
 * weight. The glyph is not decoration — it is what carries the meaning for anyone who
 * cannot separate the hues, which is why `iconOnly` still has a tooltip and an aria-label.
 */

type Props = {
    meta: StatusMeta;
    /** Matrix cells, where the word would not fit and the tooltip carries it. */
    iconOnly?: boolean;
    /** Replaces the label text outright, e.g. a roll-up's total count. */
    text?: string;
    /** Replaces the default `label — description` tooltip, e.g. a roll-up's per-status breakdown. */
    tooltip?: string;
    size?: 'xs' | 'sm' | 'md';
    'aria-label'?: string;
};

const Base = ({ meta, iconOnly, text, tooltip, size = 'sm', ...rest }: Props) => {
    const Icon = glyphIcon(meta.glyph);
    const tooltipText = tooltip ?? `${meta.label} — ${meta.description}`;
    const badge = (
        <Badge
            color={meta.color}
            variant="light"
            radius="sm"
            size={size}
            leftSection={<Icon size={14} />}
            aria-label={rest['aria-label'] ?? meta.label}
            style={meta.dimmed ? { opacity: 0.65 } : undefined}
            // A matrix cell's badge (`iconOnly`) is one of dozens on screen at once: a native
            // `title` carries the same text as the drawer/header badges' `Tooltip` without
            // Mantine's portal + positioning machinery per instance.
            {...(iconOnly ? { title: tooltipText } : {})}
        >
            {iconOnly ? '' : (text ?? meta.label)}
        </Badge>
    );

    if (iconOnly) return badge;

    return (
        <Tooltip label={tooltipText} withArrow openDelay={300}>
            {badge}
        </Tooltip>
    );
};

export const SyncStatusBadge = ({ status, ...rest }: { status: SyncStatus } & Omit<Props, 'meta'>) => (
    <Base meta={SYNC_STATUS[status]} {...rest} />
);

export const OpResultBadge = ({ result, ...rest }: { result: OpResult } & Omit<Props, 'meta'>) => (
    <Base meta={OP_RESULT[result]} {...rest} />
);

export const PlanStatusBadge = ({ status, ...rest }: { status: PlanStatus } & Omit<Props, 'meta'>) => (
    <Base meta={PLAN_STATUS[status]} {...rest} />
);

export const AuditOutcomeBadge = ({ outcome, ...rest }: { outcome: AuditOutcome } & Omit<Props, 'meta'>) => (
    <Base meta={AUDIT_OUTCOME[outcome]} {...rest} />
);

export const UserStatusBadge = ({ status, ...rest }: { status: UserStatus } & Omit<Props, 'meta'>) => (
    <Base meta={USER_STATUS[status]} {...rest} />
);
